import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  getAccessToken,
  getStoredUser,
  login as apiLogin,
  logout as apiLogout,
  refreshAccessToken,
  type MeResponse,
} from "@/lib/api";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "authenticated"; user: MeResponse }
  | { status: "anonymous"; user: null };

type AuthContextValue = AuthState & {
  login: (username: string, password: string) => Promise<MeResponse>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  useEffect(() => {
    // Au boot: hydrate depuis localStorage si un token+user y sont
    (async () => {
      const token = getAccessToken();
      const user = getStoredUser();
      if (!token || !user) {
        setState({ status: "anonymous", user: null });
        return;
      }

      // On tente un refresh silencieux pour valider que le token est encore accepté
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        setState({ status: "anonymous", user: null });
        return;
      }
      setState({ status: "authenticated", user });
    })();
  }, []);

  const login = async (username: string, password: string): Promise<MeResponse> => {
    const me = await apiLogin(username, password);
    setState({ status: "authenticated", user: me });
    return me;
  };

  const logout = () => {
    apiLogout();
    setState({ status: "anonymous", user: null });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
